import { Fragment, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Badge,
  Body1,
  Body1Strong,
  Button,
  Card,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Link,
  ProgressBar,
  Radio,
  RadioGroup,
  Tab,
  TabList,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import {
  BrainSparkleRegular,
  CheckmarkCircleRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  Dismiss24Regular,
  DocumentSearchRegular,
  DocumentTextRegular,
  EditRegular,
  WarningRegular,
} from '@fluentui/react-icons';

import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import { FoldersNavDrawer, TFolder, TPath, useItemTableStyles } from '@modules/items';
import { SignInContext, useUsers } from '@modules/users';
import type { TUser } from '@modules/users';
import { ButtonLoading, Path } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { buildHref } from '@shared/utils';

import {
  CHANGE_BEING_ASSESSED_LABEL,
  CHANGE_DEFAULT_ASSESSMENT,
  CHANGE_MOCK_CURRENT_DATE,
  CHANGE_STATUS,
} from '../constants';
import { formatMockDate } from '../helpers';
import {
  IMPLEMENTATION_ACTIVITY_DISPLAY_STATE,
  canRequestImplementationProposal,
  getCompactImplementationDependencyLabel,
  getImplementationActionType,
  getImplementationActionTypeLabel,
  getImplementationActivityDependencyIds,
  getImplementationActivityDisplayState,
  getImplementationActivityKey,
  getImplementationProposalDiffKey,
  getImplementationProposalInitialState,
  getImplementationProposalReviewGateState,
  getImplementationShortDeliverable,
  getUnresolvedImplementationDependencyIds,
  type TImplementationActivityDisplayState,
} from '../implementationPlanViewModel';
import { sanitizeImplementationProposalHtml } from '../implementationProposalSanitizer';
import type {
  ImplementationProposal,
  TChangeAssessmentConfig,
  TChangeAssessmentInformationRequest,
  TChangeAssessmentResultData,
  TChangeDashboardScope,
  TChangeImplementationPlanItem,
  TChangeMockConfig,
  TChangeMockEntry,
  TCreatedChangeRequest,
  TImplementationProposalState,
} from '../types';
import ChangeStatusBadge from './ChangeStatusBadge';

type TAssessmentPhase = 'initial-assessing' | 'questions' | 'continuation-assessing' | 'complete' | 'idle';

type TMockApprovalSignature = {
  signer: string;
  signedAt: string;
  meaning: string;
};

type TExecutionActivityStatus = 'Not started' | 'In progress' | 'Blocked' | 'Complete' | 'Completed';

type TProps = {
  folder: TFolder;
  fullPath: TPath;
  config: TChangeMockConfig;
  scope: TChangeDashboardScope;
  changeRequest?: TCreatedChangeRequest;
  existingChange?: TChangeMockEntry;
  startAiAssessment?: boolean;
};

const useStyles = makeStyles({
  content: {
    maxWidth: '1280px',
  },
  pageStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 920px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
    },
  },
  labelStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  valueText: {
    overflowWrap: 'anywhere',
  },
  ownerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  mainStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    minWidth: 0,
  },
  surface: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusLarge,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  assessmentHeaderIcon: {
    flexShrink: 0,
    fontSize: '28px',
    lineHeight: '28px',
  },
  sectionTitleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  summaryPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 920px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
    },
  },
  metricTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  metricIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase400,
  },
  deltaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 820px)': {
      gridTemplateColumns: '1fr',
    },
  },
  deltaBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  preWrap: {
    whiteSpace: 'pre-wrap',
  },
  list: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
  },
  compactList: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  compactItem: {
    display: 'grid',
    gap: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': {
      paddingBottom: 0,
      borderBottom: '0',
    },
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  inlineBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  cardDivider: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  documentsText: {
    color: tokens.colorNeutralForeground3,
  },
  documentLink: {
    color: `${tokens.colorPaletteGreenForeground1} !important`,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'underline',
    textDecorationColor: tokens.colorPaletteGreenBorder1,
    textUnderlineOffset: '2px',
    ':hover': {
      color: `${tokens.colorPaletteGreenForeground1} !important`,
      textDecorationColor: tokens.colorPaletteGreenForeground1,
    },
  },
  showMoreButton: {
    alignSelf: 'flex-start',
    width: 'fit-content',
  },
  progressMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
  progressMessage: {
    minHeight: '22px',
    color: tokens.colorNeutralForeground3,
  },
  questionList: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
  },
  questionItem: {
    display: 'grid',
    gap: tokens.spacingVerticalXS,
  },
  radioRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
  },
  compactRadio: {
    marginRight: 0,
  },
  approvalCard: {
    borderLeft: `4px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  approvalMetaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  signatureStatement: {
    alignItems: 'flex-start',
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  planTable: {
    width: '100%',
    tableLayout: 'fixed',
  },
  tableCell: {
    verticalAlign: 'top',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    overflowWrap: 'anywhere',
  },
  tableScroller: {
    width: '100%',
    overflowX: 'auto',
  },
  planPreviewList: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  planPreviewItem: {
    display: 'grid',
    gridTemplateColumns: '32px minmax(0, 1fr)',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': {
      paddingBottom: 0,
      borderBottom: '0',
    },
  },
  planPreviewNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  planPreviewMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  planMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  accordion: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  accordionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  executionSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    '@media (max-width: 920px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  executionActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  executionLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: tokens.spacingVerticalXL,
  },
  summaryProgress: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  summarySecondaryMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  readyCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },
  readyActivityCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
    padding: tokens.spacingVerticalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  primaryReadyActivityCard: {
    border: `1px solid ${tokens.colorBrandForeground1}`,
    borderLeft: `4px solid ${tokens.colorBrandForeground1}`,
    boxShadow: tokens.shadow4,
  },
  readyCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  readyCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  activityId: {
    flexShrink: 0,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  readyCardBadges: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  controlNote: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  activityList: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  activityRow: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  expandedActivityRow: {
    border: `1px solid ${tokens.colorBrandForeground1}`,
  },
  primaryActivityRow: {
    borderLeft: `4px solid ${tokens.colorBrandForeground1}`,
  },
  activityRowButton: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '56px minmax(0, 1.25fr) minmax(180px, 0.75fr) minmax(180px, 0.75fr)',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingVerticalM,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    backgroundColor: 'transparent',
    border: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
    '@media (max-width: 1020px)': {
      gridTemplateColumns: '48px minmax(0, 1fr)',
      alignItems: 'flex-start',
    },
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  activityMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  activityTitleLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  activityAssignment: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  activityStatusColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    '@media (max-width: 1020px)': {
      gridColumn: '2',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
    },
    '@media (max-width: 640px)': {
      gridColumn: '1',
    },
  },
  activityDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  selectableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  selectedRow: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  activityTitleButton: {
    justifyContent: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  },
  assigneeLayout: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  detailPanelHeader: {
    alignItems: 'flex-start',
  },
  proposalPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  proposalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  proposalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  proposalMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  evidenceList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  evidenceItem: {
    display: 'inline-flex',
    maxWidth: '100%',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    overflowWrap: 'anywhere',
  },
  proposalDiffList: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  proposalDiffItem: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'start',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
    },
  },
  proposalDiffMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  proposalGate: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
    },
  },
  proposalGateControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  proposalCompletion: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  diffDialogSurface: {
    width: 'min(1040px, calc(100vw - 48px))',
    maxWidth: '1040px',
  },
  diffDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    maxHeight: 'min(760px, calc(100vh - 180px))',
    overflowY: 'auto',
  },
  diffHeaderMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  diffHtmlViewport: {
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  diffHtmlContent: {
    maxWidth: '100%',
    minWidth: 0,
    padding: tokens.spacingVerticalM,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: tokens.lineHeightBase400,
    '& p': {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& table': {
      width: '100%',
      maxWidth: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
    },
    '& th, & td': {
      padding: tokens.spacingVerticalS,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      verticalAlign: 'top',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& del': {
      paddingLeft: '2px',
      paddingRight: '2px',
      borderRadius: tokens.borderRadiusSmall,
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorPaletteRedBackground1,
      textDecoration: 'line-through',
      textDecorationColor: tokens.colorPaletteRedForeground1,
      borderBottom: `2px solid ${tokens.colorPaletteRedBorder1}`,
    },
    '& ins': {
      paddingLeft: '2px',
      paddingRight: '2px',
      borderRadius: tokens.borderRadiusSmall,
      color: tokens.colorPaletteGreenForeground1,
      backgroundColor: tokens.colorPaletteGreenBackground1,
      textDecoration: 'none',
      borderBottom: `2px solid ${tokens.colorPaletteGreenBorder1}`,
    },
  },
});

const getMockDateValue = () => CHANGE_MOCK_CURRENT_DATE.toISOString().slice(0, 10);

const getFallbackChangeRequest = (folder: TFolder, existingChange?: TChangeMockEntry): TCreatedChangeRequest => ({
  changeNumber: existingChange?.changeNumber ?? 'CHG-2026-0043',
  changeTitle: existingChange?.changeTitle ?? 'New change request',
  product: folder.name,
  owner: existingChange?.owner ?? 'Unassigned',
  targetDate: existingChange?.targetDate,
  trigger: 'Not captured',
  description: 'No submitted request details were captured for this existing change.',
  affectedComponent: undefined,
  proposedImplementation: undefined,
  supportingEvidence: [],
  additionalComments: undefined,
  createdDate: getMockDateValue(),
});

const getCurrentMessage = (messages: string[], progressRatio: number) => {
  if (messages.length === 0) return '';
  const index = Math.min(messages.length - 1, Math.floor(progressRatio * messages.length));
  return messages[index];
};

const formatOptionalDate = (value?: string) => (value ? formatMockDate(value) : '-');

const getAssessmentConfig = (config?: TChangeAssessmentConfig): Required<TChangeAssessmentConfig> => ({
  questionDelay: config?.questionDelay ?? CHANGE_DEFAULT_ASSESSMENT.questionDelay,
  completionDelay: config?.completionDelay ?? CHANGE_DEFAULT_ASSESSMENT.completionDelay,
  messages: config?.messages?.length ? config.messages : CHANGE_DEFAULT_ASSESSMENT.messages,
  continuationMessages: config?.continuationMessages?.length
    ? config.continuationMessages
    : CHANGE_DEFAULT_ASSESSMENT.continuationMessages,
  informationRequests: config?.informationRequests ?? CHANGE_DEFAULT_ASSESSMENT.informationRequests,
});

const getPlanDeliverable = (item: TChangeImplementationPlanItem) => item.deliverable ?? item.expectedDeliverable ?? '-';

const getPlanExecutionMode = (item: TChangeImplementationPlanItem) =>
  item.executionMode ?? item.aiSupportedOrManual ?? '-';

const getPlanDependencies = (item: TChangeImplementationPlanItem) =>
  item.dependencies?.length ? item.dependencies.join(', ') : item.dependency || '-';

const MOCK_ASSESSMENT_VERSION = 'Assessment v1.0';
const MOCK_IMPLEMENTATION_PLAN_VERSION = 'Implementation plan v1.0';
const MOCK_IMPLEMENTATION_PLAN_APPROVED_VERSION = '1.0';
const CHANGE_ASSESSMENT_SIGNATURE_MEANING =
  'I approve the assessed change scope and authorise execution of the implementation plan. This approval does not authorise implementation of the product change.';
const LIST_PREVIEW_COUNT = 4;
const COMPLEX_LIST_PREVIEW_COUNT = 3;
const PLAN_PREVIEW_COUNT = 4;
const MOCK_APPROVAL_LOADING_MS = 5000;
const MOCK_IMPLEMENTATION_PROPOSAL_LOADING_MS = 15000;
const IMPLEMENTATION_PROPOSAL_ACTION_LABEL = 'Propose';
const ACTIVITY_STATUS = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  COMPLETE: 'Complete',
} as const satisfies Record<string, TExecutionActivityStatus>;

const getInitialPlanPreviewCount = (items: TChangeImplementationPlanItem[]) => {
  const lastBlockedIndex = items.reduce(
    (lastIndex, item, index) => (item.status?.toLowerCase() === 'blocked' ? index : lastIndex),
    -1
  );

  return Math.max(PLAN_PREVIEW_COUNT, lastBlockedIndex + 1);
};

const getConciseText = (value?: string, sentenceCount = 2) => {
  if (!value) return value;

  const sentences = value.match(/[^.!?]+[.!?]+/g)?.map((sentence) => sentence.trim()) ?? [];

  if (sentences.length <= sentenceCount) return value;
  if (sentenceCount === 1) return sentences[0];

  return [sentences[0], ...sentences.slice(-(sentenceCount - 1))].join(' ');
};

const getCompactText = (value?: string, maxLength = 120) => {
  const concise = getConciseText(value, 1) ?? value;
  if (!concise || concise.length <= maxLength) return concise;

  const clipped = concise
    .slice(0, maxLength)
    .replace(/\s+\S*$/, '')
    .trim();
  return `${clipped || concise.slice(0, maxLength).trim()}...`;
};

const getAffectedDocumentLabels = (data: TChangeAssessmentResultData) =>
  Array.from(new Set(data.affectedAreas?.flatMap((area) => area.documents ?? []) ?? []));

const getChangedScopeLabels = (data: TChangeAssessmentResultData) =>
  data.changeScope?.changed?.length
    ? data.changeScope.changed
    : (data.affectedAreas?.map((area) => area.area).filter((item): item is string => Boolean(item)) ?? []);

const getCountLabel = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const getSignerLabel = (user: { name?: string; mail?: string }) => user.name || user.mail || 'Current user';

const getActivityTitle = (item: TChangeImplementationPlanItem) =>
  [item.id, item.activity].filter(Boolean).join(' - ') || '-';

type TChangeDocumentLink = {
  label: string;
  href?: string;
  documentId?: string;
};

const ChangeDocumentLinksContext = createContext<TChangeDocumentLink[]>([]);

const getTargetDocumentHref = (documentId: string) => buildHref(`/${getItemPathByItemType('DOCUMENT')}/${documentId}`);

const getDocumentLinkAliases = (document?: string, documentNumber?: string, includeTitleAlias = true) => {
  const aliases = new Set<string>();
  const normalizedDocument = document?.trim();

  if (normalizedDocument) {
    aliases.add(normalizedDocument);
    aliases.add(normalizedDocument.replace(/\s+-\s+/g, ' '));

    const leadingCode = normalizedDocument.match(/^([A-Za-z]{2,}[A-Za-z0-9-]*\d[A-Za-z0-9-]*)/)?.[1];
    if (leadingCode) aliases.add(leadingCode);

    if (includeTitleAlias) {
      const titleWithoutCode = normalizedDocument
        .replace(/^[A-Za-z]{2,}[A-Za-z0-9-]*\d[A-Za-z0-9-]*\s*[- ]\s*/, '')
        .trim();
      if (titleWithoutCode && titleWithoutCode !== normalizedDocument) aliases.add(titleWithoutCode);
    }
  }

  if (documentNumber?.trim()) aliases.add(`TD${documentNumber.trim()}`);

  return Array.from(aliases).filter((alias) => alias.length >= 4);
};

const getChangeDocumentLinks = (data: TChangeAssessmentResultData): TChangeDocumentLink[] => {
  const linksByAlias = new Map<string, { label: string; documentIds: Set<string> }>();

  const addLink = (
    documentId: string | undefined,
    document?: string,
    documentNumber?: string,
    options?: { includeTitleAlias?: boolean }
  ) => {
    getDocumentLinkAliases(document, documentNumber, options?.includeTitleAlias).forEach((label) => {
      const key = label.toLocaleLowerCase();
      const existing = linksByAlias.get(key);

      if (existing) {
        if (documentId) existing.documentIds.add(documentId);
      } else {
        linksByAlias.set(key, { label, documentIds: new Set(documentId ? [documentId] : []) });
      }
    });
  };

  data.assessmentBasis?.sourcesReviewed?.forEach((source) => {
    addLink(undefined, source.document, undefined, { includeTitleAlias: false });
    addLink(undefined, [source.document, source.title].filter(Boolean).join(' - '));
  });
  data.affectedAreas?.forEach((area) => area.documents?.forEach((document) => addLink(undefined, document)));

  data.implementationPlan?.forEach((item) => {
    item.targetDocuments?.forEach((document) => addLink(document._id, document.document, document.documentNumber));
    item.implementationProposal?.sourceEvidence?.forEach((evidence) => {
      addLink(undefined, evidence.source, undefined, { includeTitleAlias: false });
      addLink(undefined, [evidence.source, evidence.title].filter(Boolean).join(' - '));
    });
    item.implementationProposal?.diffs?.forEach((diff) => {
      addLink(diff.documentId, diff.document, undefined, { includeTitleAlias: false });
    });
  });

  return Array.from(linksByAlias.values())
    .map(({ label, documentIds }) => {
      const knownDocumentIds = Array.from(documentIds);
      const documentId = knownDocumentIds.length === 1 ? knownDocumentIds[0] : undefined;

      return {
        label,
        documentId,
        href: documentId ? getTargetDocumentHref(documentId) : undefined,
      };
    })
    .sort((a, b) => b.label.length - a.label.length);
};

const isWordCharacter = (value: string | undefined) => Boolean(value && /[A-Za-z0-9]/.test(value));

const isDocumentLinkBoundary = (text: string, startIndex: number, label: string) => {
  const previous = text[startIndex - 1];
  const first = label[0];
  const last = label[label.length - 1];
  const next = text[startIndex + label.length];

  return !(isWordCharacter(previous) && isWordCharacter(first)) && !(isWordCharacter(last) && isWordCharacter(next));
};

const useChangeDocumentLinks = () => useContext(ChangeDocumentLinksContext);

const ChangeDocumentLink = ({
  href,
  documentId,
  children,
}: {
  href?: string;
  documentId?: string;
  children: ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Link
      href={href}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      className={classes.documentLink}
      data-document-reference="true"
      data-document-id={documentId}
    >
      {children}
    </Link>
  );
};

const DocumentReferenceText = ({ text }: { text: string }) => {
  const documentLinks = useChangeDocumentLinks();

  if (!documentLinks.length) return <>{text}</>;

  const nodes: ReactNode[] = [];
  let index = 0;
  let textStartIndex = 0;

  while (index < text.length) {
    const match = documentLinks.find(
      (link) => text.startsWith(link.label, index) && isDocumentLinkBoundary(text, index, link.label)
    );

    if (!match) {
      index += 1;
      continue;
    }

    if (textStartIndex < index) nodes.push(text.slice(textStartIndex, index));

    nodes.push(
      <ChangeDocumentLink
        key={`${match.documentId ?? match.label}-${index}`}
        href={match.href}
        documentId={match.documentId}
      >
        {match.label}
      </ChangeDocumentLink>
    );
    index += match.label.length;
    if (isWordCharacter(match.label.at(-1)) && isWordCharacter(text[index])) nodes.push(' ');
    textStartIndex = index;
  }

  if (textStartIndex < text.length) nodes.push(text.slice(textStartIndex));

  return <>{nodes}</>;
};

const getDependencyLabel = (dependencyId: string, planItems: TChangeImplementationPlanItem[]) => {
  const dependency = planItems.find((item) => item.id === dependencyId);
  if (!dependency) return dependencyId;

  return [dependency.id, dependency.activity].filter(Boolean).join(' - ');
};

const ACTIVITY_DISPLAY_STATE_LABELS: Record<TImplementationActivityDisplayState, string> = {
  READY: 'Ready',
  IN_PROGRESS: 'In progress',
  WAITING: 'Waiting',
  COMPLETED: 'Completed',
  BLOCKED: 'Blocked',
};

const getActivityDisplayStateBadgeColor = (state: TImplementationActivityDisplayState) => {
  switch (state) {
    case IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.BLOCKED:
      return 'danger' as const;
    case IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.IN_PROGRESS:
      return 'informative' as const;
    case IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.WAITING:
      return 'warning' as const;
    case IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.COMPLETED:
      return 'success' as const;
    case IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY:
      return 'brand' as const;
    default:
      return 'subtle' as const;
  }
};

const getActionTypeBadgeColor = (item: TChangeImplementationPlanItem) => {
  switch (getImplementationActionType(item)) {
    case 'AI_DOCUMENT_UPDATE':
      return 'brand' as const;
    case 'AI_ASSISTED':
      return 'informative' as const;
    case 'HUMAN_APPROVAL':
      return 'success' as const;
    default:
      return 'subtle' as const;
  }
};

const isAiAidedImplementationActivity = (item: TChangeImplementationPlanItem) => {
  const actionType = getImplementationActionType(item);

  return actionType === 'AI_DOCUMENT_UPDATE' || actionType === 'AI_ASSISTED';
};

const getPrimaryActivityActionLabel = (item: TChangeImplementationPlanItem) =>
  isAiAidedImplementationActivity(item) ? IMPLEMENTATION_PROPOSAL_ACTION_LABEL : 'Start activity';

const getHumanControlNote = (item: TChangeImplementationPlanItem) => {
  if (getImplementationActionType(item) === 'AI_DOCUMENT_UPDATE' && item.humanReviewRequired) {
    return 'AI prepares tracked changes. Expert review is required.';
  }

  if (item.humanReviewRequired) return 'Human review is required before completion.';

  return 'Controlled by the assigned responsible function.';
};

const getPlanStatusBadgeColor = (status?: string) => {
  switch (status?.toLocaleLowerCase()) {
    case ACTIVITY_STATUS.BLOCKED.toLocaleLowerCase():
      return 'danger' as const;
    case ACTIVITY_STATUS.IN_PROGRESS.toLocaleLowerCase():
      return 'informative' as const;
    case ACTIVITY_STATUS.COMPLETE.toLocaleLowerCase():
    case 'completed':
      return 'success' as const;
    case CHANGE_STATUS.ASSESSMENT_COMPLETE_PENDING_APPROVAL.toLocaleLowerCase():
    case 'pending approval':
    case 'pending review':
      return 'warning' as const;
    default:
      return 'subtle' as const;
  }
};

const getAssignee = (item: TChangeImplementationPlanItem, users?: TUser[]) => {
  const email = item.assigneeEmail;
  const user = email ? users?.find((user) => user.mail.toLocaleLowerCase() === email.toLocaleLowerCase()) : undefined;

  return {
    email,
    user,
    label: user?.name || user?.mail || email || 'Unassigned',
  };
};

const formatSignatureDate = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));

const noop = () => undefined;

const wait = (durationMs: number) => new Promise((resolve) => window.setTimeout(resolve, durationMs));

const SectionEditButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <Tooltip content={`Edit ${label}`} relationship="label">
    <Button
      appearance="subtle"
      icon={<EditRegular />}
      aria-label={`Edit ${label}`}
      disabled={disabled}
      onClick={onClick}
    />
  </Tooltip>
);

const ShowMoreButton = ({
  hiddenCount,
  isExpanded,
  onToggle,
}: {
  hiddenCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const classes = useStyles();

  if (hiddenCount <= 0) return null;

  return (
    <Button
      appearance="subtle"
      size="small"
      icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
      aria-expanded={isExpanded}
      onClick={onToggle}
      className={classes.showMoreButton}
    >
      {isExpanded ? 'Show less' : `Show ${getCountLabel(hiddenCount, 'more item')}`}
    </Button>
  );
};

const ProgressiveItems = <T,>({
  items,
  initialCount = COMPLEX_LIST_PREVIEW_COUNT,
  className,
  getKey,
  renderItem,
}: {
  items?: T[];
  initialCount?: number;
  className?: string;
  getKey: (item: T, index: number) => string | number;
  renderItem: (item: T, index: number) => ReactNode;
}) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items?.length) return <Body1>-</Body1>;

  const visibleItems = isExpanded ? items : items.slice(0, initialCount);
  const hiddenCount = Math.max(items.length - initialCount, 0);

  return (
    <>
      <div className={className ?? classes.compactList}>
        {visibleItems.map((item, index) => (
          <Fragment key={getKey(item, index)}>{renderItem(item, index)}</Fragment>
        ))}
      </div>
      <ShowMoreButton
        hiddenCount={hiddenCount}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((value) => !value)}
      />
    </>
  );
};

const DetailValue = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => {
  const classes = useStyles();

  return (
    <div className={mergeClasses(classes.labelStack, className)}>
      <Caption1 className={classes.muted}>{label}</Caption1>
      <Body1Strong className={classes.valueText}>
        {typeof children === 'string' ? <DocumentReferenceText text={children} /> : children || '-'}
      </Body1Strong>
    </div>
  );
};

const TextDetailValue = ({ label, value, className }: { label: string; value?: string; className?: string }) => {
  const classes = useStyles();

  return (
    <div className={mergeClasses(classes.labelStack, className)}>
      <Caption1 className={classes.muted}>{label}</Caption1>
      <Body1 className={mergeClasses(classes.valueText, classes.preWrap)}>
        {value ? <DocumentReferenceText text={value} /> : '-'}
      </Body1>
    </div>
  );
};

const SimpleList = ({ items, limit = LIST_PREVIEW_COUNT }: { items?: string[]; limit?: number }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items?.length) return <Body1>-</Body1>;

  const visibleItems = isExpanded ? items : items.slice(0, limit);
  const hiddenCount = Math.max(items.length - limit, 0);

  return (
    <>
      <ul className={classes.list}>
        {visibleItems.map((item) => (
          <li key={item}>
            <Body1>
              <DocumentReferenceText text={item} />
            </Body1>
          </li>
        ))}
      </ul>
      <ShowMoreButton
        hiddenCount={hiddenCount}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((value) => !value)}
      />
    </>
  );
};

const SummaryMetric = ({ label, value, icon }: { label: string; value: string; icon: ReactNode }) => {
  const classes = useStyles();

  return (
    <div className={classes.metricTile}>
      <div className={classes.metricHeader}>
        <span className={classes.metricIcon}>{icon}</span>
        <Caption1>{label}</Caption1>
      </div>
      <Body1Strong className={classes.valueText}>{value}</Body1Strong>
    </div>
  );
};

const KeyConclusions = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();

  if (!data.keyConclusions?.length) return null;

  return (
    <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
      <Caption1 className={classes.muted}>Key conclusions</Caption1>
      <ProgressiveItems
        items={data.keyConclusions}
        initialCount={COMPLEX_LIST_PREVIEW_COUNT}
        getKey={(item, index) => item.title ?? item.conclusion ?? index}
        renderItem={(item) => (
          <div key={item.title ?? item.conclusion} className={classes.compactItem}>
            <div className={classes.itemHeader}>
              <Body1Strong>{item.title || '-'}</Body1Strong>
              {item.confidence && <Badge appearance="tint">{item.confidence}</Badge>}
            </div>
            {item.conclusion && (
              <Body1>
                <DocumentReferenceText text={item.conclusion} />
              </Body1>
            )}
          </div>
        )}
      />
    </div>
  );
};

const AffectedAreas = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();

  if (!data.affectedAreas?.length) return null;

  return (
    <Card className={classes.surface} size="large">
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitleStack}>
          <Subtitle2>Affected areas</Subtitle2>
          <Caption1 className={classes.muted}>{getCountLabel(data.affectedAreas.length, 'area')}</Caption1>
        </div>
      </div>
      <ProgressiveItems
        items={data.affectedAreas}
        initialCount={COMPLEX_LIST_PREVIEW_COUNT}
        getKey={(item, index) => item.area ?? item.rationale ?? index}
        renderItem={(item) => (
          <div key={item.area ?? item.rationale} className={classes.compactItem}>
            <div className={classes.itemHeader}>
              <Body1Strong>{item.area || '-'}</Body1Strong>
              {item.impact && <Badge appearance="tint">{item.impact}</Badge>}
            </div>
            {item.rationale && (
              <Caption1 className={classes.documentsText}>
                <DocumentReferenceText text={item.rationale} />
              </Caption1>
            )}
            {item.documents?.length ? (
              <Caption1 className={classes.documentsText}>
                <DocumentReferenceText text={item.documents.join(', ')} />
              </Caption1>
            ) : null}
          </div>
        )}
      />
    </Card>
  );
};

const EvidenceGaps = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();

  if (!data.evidenceGaps?.length) return null;

  return (
    <ProgressiveItems
      items={data.evidenceGaps}
      initialCount={COMPLEX_LIST_PREVIEW_COUNT}
      getKey={(gap, index) => gap.id ?? gap.title ?? index}
      renderItem={(gap) => (
        <div key={gap.id ?? gap.title} className={classes.compactItem}>
          <div className={classes.itemHeader}>
            <Body1Strong>{[gap.id, gap.title].filter(Boolean).join(' - ') || '-'}</Body1Strong>
            <div className={classes.inlineBadges}>
              {gap.severity && (
                <Badge appearance="tint" color={gap.severity === 'Blocking' ? 'danger' : 'warning'}>
                  {gap.severity}
                </Badge>
              )}
              {gap.blocks && <Badge appearance="tint">{gap.blocks}</Badge>}
            </div>
          </div>
          {gap.description && (
            <Caption1 className={classes.documentsText}>
              <DocumentReferenceText text={gap.description} />
            </Caption1>
          )}
          {gap.requiredAction && (
            <Caption1 className={classes.documentsText}>
              Required action: <DocumentReferenceText text={gap.requiredAction} />
            </Caption1>
          )}
        </div>
      )}
    />
  );
};

const NoImpactDecisions = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();

  if (!data.reviewedNoChangeAreas?.length) return null;

  return (
    <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
      <Caption1 className={classes.muted}>Reviewed no-change areas</Caption1>
      <ProgressiveItems
        items={data.reviewedNoChangeAreas}
        initialCount={COMPLEX_LIST_PREVIEW_COUNT}
        getKey={(item, index) => item.area ?? item.conclusion ?? index}
        renderItem={(item) => (
          <div key={item.area ?? item.conclusion} className={classes.compactItem}>
            <div className={classes.itemHeader}>
              <Body1Strong>{item.area || '-'}</Body1Strong>
              {item.conclusion && (
                <Caption1 className={classes.documentsText}>
                  <DocumentReferenceText text={item.conclusion} />
                </Caption1>
              )}
            </div>
            {item.rationale && (
              <Body1>
                <DocumentReferenceText text={item.rationale} />
              </Body1>
            )}
          </div>
        )}
      />
    </div>
  );
};

const SafetyPerformanceImpactDetails = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();
  const impact = data.safetyPerformanceImpact;

  if (!impact) return null;

  if (typeof impact === 'string') {
    return <TextDetailValue label="Safety and performance impact" value={impact} className={classes.fullWidth} />;
  }

  return (
    <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
      <Caption1 className={classes.muted}>Safety and performance impact</Caption1>
      <div className={classes.compactList}>
        {impact.level && <Body1Strong>{impact.level}</Body1Strong>}
        {impact.summary && (
          <Body1>
            <DocumentReferenceText text={impact.summary} />
          </Body1>
        )}
        {impact.potentialImpacts?.length ? (
          <div className={classes.labelStack}>
            <Caption1 className={classes.muted}>Potential impacts</Caption1>
            <SimpleList items={impact.potentialImpacts} />
          </div>
        ) : null}
        {impact.requiredControls?.length ? (
          <div className={classes.labelStack}>
            <Caption1 className={classes.muted}>Required controls</Caption1>
            <SimpleList items={impact.requiredControls} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const REGULATORY_IMPACT_LABELS = [
  ['classification', 'Classification'],
  ['intendedPurpose', 'Intended purpose'],
  ['commercialReference', 'Commercial reference'],
  ['basicUdiDi', 'Basic UDI-DI'],
  ['declarationOfConformity', 'Declaration of conformity'],
  ['notifiedBodyNotification', 'Notified body notification'],
] as const;

const RegulatoryImpactDetails = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();
  const impact = data.regulatoryImpact;

  if (!impact) return null;

  if (typeof impact === 'string') {
    return <TextDetailValue label="Regulatory impact" value={impact} className={classes.fullWidth} />;
  }

  const reviews = REGULATORY_IMPACT_LABELS.map(([key, label]) => ({
    key,
    label,
    review: impact[key],
  })).filter(({ review }) => review?.conclusion || review?.rationale || review?.reviewRequired);

  if (!impact.overallConclusion && reviews.length === 0) return null;

  return (
    <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
      <Caption1 className={classes.muted}>Regulatory impact</Caption1>
      <div className={classes.compactList}>
        {impact.overallConclusion && (
          <Body1>
            <DocumentReferenceText text={impact.overallConclusion} />
          </Body1>
        )}
        {reviews.length ? (
          <ProgressiveItems
            items={reviews}
            initialCount={COMPLEX_LIST_PREVIEW_COUNT}
            getKey={(item) => item.key}
            renderItem={({ label, review }) => (
              <div className={classes.compactItem}>
                <div className={classes.itemHeader}>
                  <Body1Strong>{label}</Body1Strong>
                  {review?.reviewRequired && <Badge appearance="tint">Review required</Badge>}
                </div>
                {review?.conclusion && (
                  <Body1>
                    <DocumentReferenceText text={review.conclusion} />
                  </Body1>
                )}
                {review?.rationale && (
                  <Caption1 className={classes.documentsText}>
                    <DocumentReferenceText text={review.rationale} />
                  </Caption1>
                )}
              </div>
            )}
          />
        ) : null}
      </div>
    </div>
  );
};

const AssessmentDetailsAccordion = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();
  const hasScope = Boolean(data.changeScope?.changed?.length || data.changeScope?.unchanged?.length);
  const hasImpactDetails = Boolean(data.safetyPerformanceImpact || data.regulatoryImpact);
  const hasEvidenceGaps = Boolean(data.evidenceGaps?.length);
  const hasReviewedNoChange = Boolean(data.reviewedNoChangeAreas?.length || data.assumptions?.length);
  const hasBasis = Boolean(
    data.assessmentBasis?.informationProvided?.length || data.assessmentBasis?.sourcesReviewed?.length
  );
  const hasTraceability = Boolean(data.traceabilityPreview?.length);

  if (!hasImpactDetails && !hasScope && !hasEvidenceGaps && !hasReviewedNoChange && !hasBasis && !hasTraceability) {
    return null;
  }

  return (
    <Accordion collapsible multiple className={mergeClasses(classes.fullWidth, classes.accordion)}>
      {hasImpactDetails && (
        <AccordionItem value="impact">
          <AccordionHeader>Impact details</AccordionHeader>
          <AccordionPanel>
            <div className={classes.accordionPanel}>
              <SafetyPerformanceImpactDetails data={data} />
              <RegulatoryImpactDetails data={data} />
            </div>
          </AccordionPanel>
        </AccordionItem>
      )}

      {hasScope && (
        <AccordionItem value="scope">
          <AccordionHeader>
            Change scope ({getCountLabel(data.changeScope?.changed?.length ?? 0, 'changed item')},{' '}
            {getCountLabel(data.changeScope?.unchanged?.length ?? 0, 'unchanged item')})
          </AccordionHeader>
          <AccordionPanel>
            <div className={classes.accordionPanel}>
              <div>
                <Caption1 className={classes.muted}>Changed</Caption1>
                <SimpleList items={data.changeScope?.changed} />
              </div>
              <div>
                <Caption1 className={classes.muted}>Unchanged</Caption1>
                <SimpleList items={data.changeScope?.unchanged} />
              </div>
            </div>
          </AccordionPanel>
        </AccordionItem>
      )}

      {hasEvidenceGaps && (
        <AccordionItem value="evidence-gaps">
          <AccordionHeader>Evidence gaps ({getCountLabel(data.evidenceGaps?.length ?? 0, 'gap')})</AccordionHeader>
          <AccordionPanel>
            <EvidenceGaps data={data} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {hasReviewedNoChange && (
        <AccordionItem value="no-change">
          <AccordionHeader>Reviewed as unchanged</AccordionHeader>
          <AccordionPanel>
            <div className={classes.accordionPanel}>
              <NoImpactDecisions data={data} />
              {data.assumptions?.length ? (
                <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
                  <Caption1 className={classes.muted}>Assumptions</Caption1>
                  <SimpleList items={data.assumptions} />
                </div>
              ) : null}
            </div>
          </AccordionPanel>
        </AccordionItem>
      )}

      {hasBasis && (
        <AccordionItem value="basis">
          <AccordionHeader>Assessment basis</AccordionHeader>
          <AccordionPanel>
            <div className={classes.accordionPanel}>
              <div>
                <Caption1 className={classes.muted}>Information provided</Caption1>
                <SimpleList items={data.assessmentBasis?.informationProvided} />
              </div>
              {data.assessmentBasis?.sourcesReviewed?.length ? (
                <ProgressiveItems
                  items={data.assessmentBasis.sourcesReviewed}
                  initialCount={COMPLEX_LIST_PREVIEW_COUNT}
                  getKey={(source, index) => `${source.document}-${source.title}-${index}`}
                  renderItem={(source) => (
                    <div className={classes.compactItem}>
                      <Body1Strong>
                        <DocumentReferenceText text={[source.document, source.title].filter(Boolean).join(' - ')} />
                      </Body1Strong>
                      {source.finding && (
                        <Body1>
                          <DocumentReferenceText text={source.finding} />
                        </Body1>
                      )}
                    </div>
                  )}
                />
              ) : null}
            </div>
          </AccordionPanel>
        </AccordionItem>
      )}

      {hasTraceability && (
        <AccordionItem value="traceability">
          <AccordionHeader>
            Traceability preview ({getCountLabel(data.traceabilityPreview?.length ?? 0, 'link')})
          </AccordionHeader>
          <AccordionPanel>
            <ProgressiveItems
              items={data.traceabilityPreview}
              initialCount={COMPLEX_LIST_PREVIEW_COUNT}
              getKey={(item, index) => `${item.risk}-${item.requirement}-${index}`}
              renderItem={(item) => (
                <div className={classes.compactItem}>
                  <Body1Strong>{item.risk ? <DocumentReferenceText text={item.risk} /> : '-'}</Body1Strong>
                  <Caption1 className={classes.documentsText}>
                    Control: {item.riskControl ? <DocumentReferenceText text={item.riskControl} /> : '-'}
                  </Caption1>
                  <Caption1 className={classes.documentsText}>
                    Requirement: {item.requirement ? <DocumentReferenceText text={item.requirement} /> : '-'}
                  </Caption1>
                  <Caption1 className={classes.documentsText}>
                    Verification: {item.verification ? <DocumentReferenceText text={item.verification} /> : '-'}
                  </Caption1>
                </div>
              )}
            />
          </AccordionPanel>
        </AccordionItem>
      )}
    </Accordion>
  );
};

const AssessmentResultContent = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();
  const affectedDocuments = getAffectedDocumentLabels(data);
  const changedScope = getChangedScopeLabels(data);
  const evidenceGapCount = data.evidenceGaps?.length ?? 0;
  const implementationTaskCount = data.implementationPlan?.length ?? 0;

  return (
    <>
      <Card className={classes.surface} size="large">
        <div className={classes.sectionHeader}>
          <div className={classes.sectionTitleStack}>
            <Subtitle2>Assessment</Subtitle2>
            <Caption1 className={classes.muted}>Summary and key impacts.</Caption1>
          </div>
          <div className={classes.headerActions}>
            <div className={classes.inlineBadges}>
              {data.impactLevel && <Badge appearance="tint">{data.impactLevel} impact</Badge>}
              {data.changeType && <Badge appearance="outline">{data.changeType}</Badge>}
            </div>
            <SectionEditButton label="Assessment" />
          </div>
        </div>

        {data.assessmentSummary && (
          <div className={classes.summaryPanel}>
            <Caption1 className={classes.muted}>Assessment summary</Caption1>
            <Body1 className={mergeClasses(classes.valueText, classes.preWrap)}>
              <DocumentReferenceText text={getConciseText(data.assessmentSummary) ?? data.assessmentSummary} />
            </Body1>
          </div>
        )}

        <div className={classes.metricGrid}>
          <SummaryMetric
            label="Changed scope"
            value={changedScope.length ? getCountLabel(changedScope.length, 'item') : '-'}
            icon={<CheckmarkCircleRegular />}
          />
          <SummaryMetric
            label="Affected documents"
            value={affectedDocuments.length ? getCountLabel(affectedDocuments.length, 'document') : '-'}
            icon={<DocumentTextRegular />}
          />
          <SummaryMetric
            label="Evidence gaps"
            value={getCountLabel(evidenceGapCount, 'gap')}
            icon={<WarningRegular />}
          />
          <SummaryMetric
            label="Plan tasks"
            value={getCountLabel(implementationTaskCount, 'task')}
            icon={<BrainSparkleRegular />}
          />
        </div>

        <div className={classes.deltaGrid}>
          <div className={classes.deltaBlock}>
            <Caption1 className={classes.muted}>What changes</Caption1>
            <SimpleList items={changedScope} limit={5} />
          </div>
          <div className={classes.deltaBlock}>
            <Caption1 className={classes.muted}>Affected documents</Caption1>
            <SimpleList items={affectedDocuments} limit={5} />
          </div>
        </div>

        <KeyConclusions data={data} />
        <AssessmentDetailsAccordion data={data} />
      </Card>

      <AffectedAreas data={data} />
    </>
  );
};

const ImplementationPlanContent = ({ data }: { data: TChangeAssessmentResultData }) => {
  const classes = useStyles();
  const tableClasses = useItemTableStyles();
  const planItems = data.implementationPlan ?? [];
  const initialPlanPreviewCount = getInitialPlanPreviewCount(planItems);

  return (
    <div className={classes.mainStack}>
      {planItems.length ? (
        <Card className={classes.surface} size="large">
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitleStack}>
              <Subtitle2>Implementation plan</Subtitle2>
              <Caption1 className={classes.muted}>{getCountLabel(planItems.length, 'task')}</Caption1>
            </div>
            <SectionEditButton label="Implementation plan" />
          </div>

          <ProgressiveItems
            items={planItems}
            initialCount={initialPlanPreviewCount}
            className={classes.planPreviewList}
            getKey={(item, index) => item.id ?? `${item.activity}-${item.responsibleFunction}-${index}`}
            renderItem={(item, index) => (
              <div className={classes.planPreviewItem}>
                <div className={classes.planPreviewNumber}>{index + 1}</div>
                <div className={classes.planPreviewMain}>
                  <div className={classes.itemHeader}>
                    <Body1Strong className={classes.valueText}>
                      <DocumentReferenceText text={item.activity} />
                    </Body1Strong>
                    {item.status && (
                      <Badge appearance="tint" color={getPlanStatusBadgeColor(item.status)}>
                        {item.status}
                      </Badge>
                    )}
                  </div>
                  <div className={classes.planMetaRow}>
                    <Caption1>{item.responsibleFunction}</Caption1>
                    <Caption1>
                      <DocumentReferenceText text={getPlanExecutionMode(item)} />
                    </Caption1>
                  </div>
                  <Body1>
                    <DocumentReferenceText text={getPlanDeliverable(item)} />
                  </Body1>
                  {item.completionCriterion && (
                    <Caption1 className={classes.documentsText}>
                      <DocumentReferenceText text={item.completionCriterion} />
                    </Caption1>
                  )}
                </div>
              </div>
            )}
          />

          <Accordion collapsible className={classes.accordion}>
            <AccordionItem value="full-plan">
              <AccordionHeader>Full implementation plan</AccordionHeader>
              <AccordionPanel>
                <div className={classes.tableScroller}>
                  <Table className={mergeClasses(tableClasses.table, classes.planTable)}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell style={{ width: '24%' }}>
                          <Body1Strong>Activity</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '17%' }}>
                          <Body1Strong>Responsible function</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '23%' }}>
                          <Body1Strong>Expected deliverable</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '16%' }}>
                          <Body1Strong>Execution mode</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell>
                          <Body1Strong>Dependencies</Body1Strong>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody className={tableClasses.tableBody}>
                      {planItems.map((item) => (
                        <TableRow key={item.id ?? `${item.activity}-${item.responsibleFunction}`}>
                          <TableCell className={classes.tableCell}>
                            <DocumentReferenceText text={item.activity} />
                          </TableCell>
                          <TableCell className={classes.tableCell}>{item.responsibleFunction}</TableCell>
                          <TableCell className={classes.tableCell}>
                            <div className={classes.labelStack}>
                              <Body1>
                                <DocumentReferenceText text={getPlanDeliverable(item)} />
                              </Body1>
                              {item.completionCriterion && (
                                <Caption1 className={classes.documentsText}>
                                  <DocumentReferenceText text={item.completionCriterion} />
                                </Caption1>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <DocumentReferenceText text={getPlanExecutionMode(item)} />
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <div className={classes.labelStack}>
                              <Body1>
                                <DocumentReferenceText text={getPlanDependencies(item)} />
                              </Body1>
                              {item.status && (
                                <Badge appearance="tint" color={getPlanStatusBadgeColor(item.status)}>
                                  {item.status}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Card>
      ) : (
        <Card className={classes.surface} size="large">
          <div className={classes.sectionHeader}>
            <Subtitle2>Implementation plan</Subtitle2>
            <SectionEditButton label="Implementation plan" />
          </div>
          <Body1>-</Body1>
        </Card>
      )}
    </div>
  );
};

const formatSignatureTimestamp = (value: string) =>
  `${new Date(value).toISOString().replace('T', ' ').substring(0, 19)} (UTC+00:00)`;

const InformationRequestsCard = ({
  requests,
  answers,
  onAnswerChange,
  onContinue,
}: {
  requests: TChangeAssessmentInformationRequest[];
  answers: Record<string, string>;
  onAnswerChange: (id: string, value: string) => void;
  onContinue: () => void;
}) => {
  const classes = useStyles();
  const isComplete = requests.every((request) => !request.required || Boolean(answers[request.id]));

  return (
    <Card className={classes.surface} size="large">
      <Subtitle2>Additional information required</Subtitle2>
      <div className={classes.questionList}>
        {requests.map((request) => (
          <div key={request.id} className={classes.questionItem}>
            <Caption1 className={classes.muted}>{request.label}</Caption1>
            <Field label={request.question} required={request.required}>
              <RadioGroup
                value={answers[request.id] ?? ''}
                onChange={(_, data) => onAnswerChange(request.id, data.value)}
                className={classes.radioRow}
              >
                {request.options.map((option) => (
                  <Radio key={option} value={option} label={option} className={classes.compactRadio} />
                ))}
              </RadioGroup>
            </Field>
          </div>
        ))}
      </div>
      <div className={classes.actionRow}>
        <Button appearance="primary" disabled={!isComplete} onClick={onContinue}>
          Continue assessment
        </Button>
      </div>
    </Card>
  );
};

const AssessmentApprovalCard = ({
  signature,
  onApproveForExecution,
  changeRequest,
  requiredSigner,
  isSigning,
}: {
  signature?: TMockApprovalSignature;
  onApproveForExecution: () => void;
  changeRequest: TCreatedChangeRequest;
  requiredSigner: string;
  isSigning: boolean;
}) => {
  const classes = useStyles();
  const [acceptance, setAcceptance] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const handleApproveClick = () => {
    setAttemptedSubmit(true);
    if (!acceptance || isSigning) return;

    onApproveForExecution();
  };

  if (signature) {
    return (
      <Card className={mergeClasses(classes.surface, classes.approvalCard)} size="large">
        <div className={classes.sectionHeader}>
          <Subtitle2>Approval</Subtitle2>
          <CheckmarkCircleRegular />
        </div>
        <div className={classes.approvalMetaGrid}>
          <DetailValue label="Change number">{changeRequest.changeNumber}</DetailValue>
          <DetailValue label="Change title">{changeRequest.changeTitle}</DetailValue>
          <DetailValue label="Versions approved">
            {[MOCK_ASSESSMENT_VERSION, MOCK_IMPLEMENTATION_PLAN_VERSION].join(', ')}
          </DetailValue>
          <DetailValue label="Required signer">{requiredSigner}</DetailValue>
          <DetailValue label="Signed by">{signature.signer}</DetailValue>
          <DetailValue label="Date and time">{formatSignatureTimestamp(signature.signedAt)}</DetailValue>
          <DetailValue label="Signature status">Signed</DetailValue>
          <TextDetailValue label="Signature meaning" value={signature.meaning} className={classes.fullWidth} />
        </div>
      </Card>
    );
  }

  return (
    <Card className={classes.surface} size="large">
      <div className={classes.sectionHeader}>
        <Subtitle2>Approval</Subtitle2>
        <SectionEditButton label="Approval" disabled={isSigning} onClick={handleApproveClick} />
      </div>
      <div className={classes.approvalMetaGrid}>
        <DetailValue label="Change number">{changeRequest.changeNumber}</DetailValue>
        <DetailValue label="Change title">{changeRequest.changeTitle}</DetailValue>
        <DetailValue label="Versions to approve">
          {[MOCK_ASSESSMENT_VERSION, MOCK_IMPLEMENTATION_PLAN_VERSION].join(', ')}
        </DetailValue>
        <DetailValue label="Required signer">{requiredSigner}</DetailValue>
        <DetailValue label="Signature status">Pending</DetailValue>
        <div className={mergeClasses(classes.labelStack, classes.fullWidth)}>
          <Caption1 className={classes.muted}>Signature meaning</Caption1>
          <Checkbox
            className={classes.signatureStatement}
            checked={acceptance}
            onChange={(_, data) => setAcceptance(data.checked === true)}
            label={CHANGE_ASSESSMENT_SIGNATURE_MEANING}
          />
          {attemptedSubmit && !acceptance && (
            <Caption1 className={classes.documentsText}>Confirm the signature meaning to continue.</Caption1>
          )}
        </div>
      </div>
      <div className={classes.actionRow}>
        <ButtonLoading appearance="primary" loading={isSigning} onClick={handleApproveClick}>
          Approve for execution
        </ButtonLoading>
      </div>
    </Card>
  );
};

const AssigneeDisplay = ({ item, users }: { item: TChangeImplementationPlanItem; users?: TUser[] }) => {
  const classes = useStyles();
  const assignee = getAssignee(item, users);

  return (
    <div className={classes.assigneeLayout}>
      <Avatar name={assignee.label} color="colorful" size={28} />
      <div className={classes.labelStack}>
        <Body1Strong className={classes.valueText}>{assignee.label}</Body1Strong>
        {assignee.email && <Caption1 className={classes.muted}>{assignee.email}</Caption1>}
      </div>
    </div>
  );
};

type TImplementationActivityView = {
  item: TChangeImplementationPlanItem;
  key: string;
  displayState: TImplementationActivityDisplayState;
  dependencyIds: string[];
  unresolvedDependencyIds: string[];
  dependencyLabels: string[];
  compactDependencyLabel: string;
};

const ActivityStateBadge = ({ state }: { state: TImplementationActivityDisplayState }) => (
  <Badge appearance="tint" color={getActivityDisplayStateBadgeColor(state)}>
    {ACTIVITY_DISPLAY_STATE_LABELS[state]}
  </Badge>
);

const ActionTypeBadge = ({ item }: { item: TChangeImplementationPlanItem }) => (
  <Badge appearance="tint" color={getActionTypeBadgeColor(item)}>
    {getImplementationActionTypeLabel(item)}
  </Badge>
);

type TImplementationProposalDiff = NonNullable<ImplementationProposal['diffs']>[number];
type TProposalDiffTab = 'tracked' | 'before' | 'after';

const getProposalDiffTabs = (diff: TImplementationProposalDiff) =>
  [
    { value: 'tracked' as const, label: 'Tracked changes', html: diff.diffHtml },
    { value: 'before' as const, label: 'Before', html: diff.beforeHtml },
    { value: 'after' as const, label: 'After', html: diff.afterHtml },
  ].filter((tab) => Boolean(tab.html));

const ImplementationProposalHtmlContent = ({ html }: { html?: string }) => {
  const classes = useStyles();
  const sanitizedHtml = sanitizeImplementationProposalHtml(html);

  if (!sanitizedHtml) return <Body1>No HTML content is available for this view.</Body1>;

  return (
    <div
      className={classes.diffHtmlViewport}
      data-testid="implementation-proposal-diff-viewport"
      style={{ maxWidth: '100%', overflowX: 'auto' }}
    >
      <div
        className={classes.diffHtmlContent}
        data-testid="implementation-proposal-diff-content"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
};

export const ImplementationProposalDiffDialog = ({
  selectedDiff,
  onClose,
  actions,
}: {
  selectedDiff?: {
    diff: TImplementationProposalDiff;
    diffKey: string;
  };
  onClose: () => void;
  actions?: ReactNode;
}) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<TProposalDiffTab>('tracked');
  const diff = selectedDiff?.diff;
  const tabs = diff ? getProposalDiffTabs(diff) : [];
  const activeTab = tabs.find((tab) => tab.value === selectedTab) ?? tabs[0];

  useEffect(() => {
    if (!selectedDiff) return;

    const nextTabs = getProposalDiffTabs(selectedDiff.diff);
    setSelectedTab(nextTabs.some((tab) => tab.value === 'tracked') ? 'tracked' : (nextTabs[0]?.value ?? 'tracked'));
  }, [selectedDiff?.diffKey]);

  return (
    <Dialog
      open={Boolean(selectedDiff)}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={classes.diffDialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {diff?.title}
          </DialogTitle>
          <DialogContent className={classes.diffDialogContent}>
            {diff && (
              <>
                <div className={classes.proposalSection}>
                  <Body1Strong>{diff.document ? <DocumentReferenceText text={diff.document} /> : '-'}</Body1Strong>
                  <div className={classes.diffHeaderMeta}>
                    {diff.section && <Caption1>{diff.section}</Caption1>}
                    {diff.confidence && <Badge appearance="tint">{`Confidence: ${diff.confidence}`}</Badge>}
                    {diff.humanReviewRequired && (
                      <Badge appearance="tint" color="warning">
                        Expert review required
                      </Badge>
                    )}
                  </div>
                  {diff.rationale && (
                    <Body1 className={classes.valueText}>
                      <DocumentReferenceText text={diff.rationale} />
                    </Body1>
                  )}
                </div>

                {tabs.length > 1 && (
                  <TabList
                    selectedValue={activeTab?.value}
                    onTabSelect={(_: SelectTabEvent, data: SelectTabData) =>
                      setSelectedTab(data.value as TProposalDiffTab)
                    }
                  >
                    {tabs.map((tab) => (
                      <Tab key={tab.value} id={`proposal-diff-tab-${tab.value}`} value={tab.value}>
                        {tab.label}
                      </Tab>
                    ))}
                  </TabList>
                )}

                <div role="tabpanel" aria-labelledby={activeTab ? `proposal-diff-tab-${activeTab.value}` : undefined}>
                  <ImplementationProposalHtmlContent html={activeTab?.html} />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {actions ?? (
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Close</Button>
              </DialogTrigger>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const ImplementationProposalPanel = ({
  proposal,
  viewedDiffIds,
  consentChecked,
  onOpenDiff,
  onConsentChange,
  onAccept,
  onClose,
}: {
  proposal: ImplementationProposal;
  viewedDiffIds: string[];
  consentChecked: boolean;
  onOpenDiff: (diff: TImplementationProposalDiff, diffKey: string) => void;
  onConsentChange: (checked: boolean) => void;
  onAccept: () => void;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const diffs = proposal.diffs ?? [];
  const gateState = getImplementationProposalReviewGateState({
    proposal,
    viewedDiffIds,
    consentChecked,
  });
  const viewedDiffIdSet = new Set(viewedDiffIds);
  const requiredDiffCount = gateState.requiredDiffIds.length || (proposal.reviewGate?.requireDiffOpened ? 1 : 0);
  const viewedRequiredDiffCount = gateState.requiredDiffIds.length
    ? gateState.requiredDiffIds.filter((diffId) => viewedDiffIdSet.has(diffId)).length
    : viewedDiffIds.length;
  const sourceCount = proposal.sourceEvidence?.length ?? 0;
  const reviewConsentLabel =
    requiredDiffCount === 1
      ? 'I reviewed the required diff.'
      : requiredDiffCount > 1
        ? 'I reviewed all required diffs.'
        : 'I reviewed the proposal.';

  return (
    <div className={classes.proposalPanel}>
      <div className={classes.proposalHeader}>
        <div className={classes.proposalSection}>
          <div className={classes.activityTitleLine}>
            <BrainSparkleRegular />
            <Subtitle2>{proposal.title}</Subtitle2>
          </div>
          <div className={classes.proposalMetaRow}>
            <Badge appearance="tint" color="brand">
              {getCountLabel(diffs.length, 'diff')}
            </Badge>
            {sourceCount > 0 && (
              <Badge appearance="tint" color="brand">
                {getCountLabel(sourceCount, 'source')}
              </Badge>
            )}
            <Badge appearance="tint" color="warning">
              Expert review
            </Badge>
          </div>
          {proposal.summary && (
            <Caption1 className={classes.muted}>
              <DocumentReferenceText text={getCompactText(proposal.summary) ?? proposal.summary} />
            </Caption1>
          )}
        </div>
        <Tooltip content="Close proposal review" relationship="label">
          <Button
            appearance="subtle"
            aria-label="Close proposal review"
            icon={<Dismiss24Regular />}
            onClick={onClose}
          />
        </Tooltip>
      </div>

      {proposal.sourceEvidence?.length ? (
        <div className={classes.proposalSection}>
          <Body1Strong>Evidence</Body1Strong>
          <div className={classes.evidenceList}>
            {proposal.sourceEvidence.map((evidence, index) => (
              <Body1Strong key={`${evidence.source ?? 'source'}-${index}`} className={classes.evidenceItem}>
                <DocumentReferenceText text={[evidence.source, evidence.title].filter(Boolean).join(' - ')} />
              </Body1Strong>
            ))}
          </div>
        </div>
      ) : null}

      <div className={classes.proposalSection}>
        <Body1Strong>Review changes</Body1Strong>
        <div className={classes.proposalDiffList}>
          {diffs.map((diff, index) => {
            const diffKey = getImplementationProposalDiffKey(diff, index);
            const isViewed = viewedDiffIdSet.has(diffKey);
            const canOpenDiff = diff.format === 'HTML' && Boolean(diff.diffHtml);

            return (
              <div
                key={diffKey}
                className={classes.proposalDiffItem}
                data-diff-id={diffKey}
                data-viewed={isViewed ? 'true' : 'false'}
              >
                <div className={classes.proposalSection}>
                  <Body1Strong className={classes.valueText}>
                    {diff.title ? <DocumentReferenceText text={diff.title} /> : '-'}
                  </Body1Strong>
                  <div className={classes.proposalDiffMeta}>
                    {diff.document && (
                      <Caption1>
                        <DocumentReferenceText text={diff.document} />
                      </Caption1>
                    )}
                    {diff.section && <Caption1>{diff.section}</Caption1>}
                    {diff.confidence && <Badge appearance="tint">{`Confidence: ${diff.confidence}`}</Badge>}
                    {diff.humanReviewRequired && (
                      <Badge appearance="tint" color="warning">
                        Expert review required
                      </Badge>
                    )}
                    <Badge appearance="tint" color={isViewed ? 'success' : 'warning'}>
                      {isViewed ? 'Opened' : 'Not opened'}
                    </Badge>
                  </div>
                </div>
                <Button
                  appearance="secondary"
                  icon={<DocumentSearchRegular />}
                  disabled={!canOpenDiff}
                  onClick={() => onOpenDiff(diff, diffKey)}
                >
                  View diff
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.proposalGate}>
        <div className={classes.proposalGateControls}>
          {requiredDiffCount > 0 && (
            <Caption1 className={classes.muted}>
              {`${Math.min(viewedRequiredDiffCount, requiredDiffCount)} of ${requiredDiffCount} required diff${
                requiredDiffCount === 1 ? '' : 's'
              } opened`}
            </Caption1>
          )}
          {gateState.consentRequired && proposal.reviewGate?.consentLabel ? (
            <Checkbox
              checked={gateState.requiredDiffReviewComplete && consentChecked}
              disabled={!gateState.requiredDiffReviewComplete}
              label={reviewConsentLabel}
              onChange={(_, data) => onConsentChange(gateState.requiredDiffReviewComplete && data.checked === true)}
            />
          ) : null}
        </div>
        {proposal.reviewGate?.acceptActionLabel ? (
          <Button appearance="primary" disabled={!gateState.canAccept} onClick={onAccept}>
            {proposal.reviewGate.acceptActionLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const getActivityActionLabel = (activity: TImplementationActivityView) =>
  canRequestImplementationProposal(activity.item, activity.displayState)
    ? IMPLEMENTATION_PROPOSAL_ACTION_LABEL
    : activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY
      ? getPrimaryActivityActionLabel(activity.item)
      : ACTIVITY_DISPLAY_STATE_LABELS[activity.displayState];

const getReadyActivityActionLabel = (activity: TImplementationActivityView) =>
  canRequestImplementationProposal(activity.item, activity.displayState)
    ? IMPLEMENTATION_PROPOSAL_ACTION_LABEL
    : getPrimaryActivityActionLabel(activity.item);

const getActivityActionIcon = (activity: TImplementationActivityView) => {
  const canShowProposalIcon =
    canRequestImplementationProposal(activity.item, activity.displayState) ||
    (activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY &&
      isAiAidedImplementationActivity(activity.item));

  return canShowProposalIcon ? <BrainSparkleRegular /> : undefined;
};

const isProposalReviewVisible = (
  proposalState: TImplementationProposalState | undefined,
  activeProposalActivityKey: string | undefined,
  activityKey: string
) => proposalState === 'READY_FOR_REVIEW' && activeProposalActivityKey === activityKey;

const getAcceptedImplementationStatus = (proposal?: ImplementationProposal): string =>
  proposal?.reviewGate?.acceptedStatus ?? ACTIVITY_STATUS.COMPLETE;

const getInitialImplementationProposalStates = (planItems: TChangeImplementationPlanItem[]) =>
  planItems.reduce<Record<string, TImplementationProposalState>>((states, item, index) => {
    if (!item.implementationProposal?.available) return states;

    states[getImplementationActivityKey(item, index)] = getImplementationProposalInitialState(
      item.implementationProposal
    );
    return states;
  }, {});

const getActivityActionEnabled = (activity: TImplementationActivityView) =>
  canRequestImplementationProposal(activity.item, activity.displayState) ||
  activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY;

export const ImplementationPlanExecutionView = ({
  data,
  changeRequest,
  signature,
}: {
  data: TChangeAssessmentResultData;
  changeRequest: TCreatedChangeRequest;
  signature: TMockApprovalSignature;
}) => {
  const classes = useStyles();
  const { users } = useUsers();
  const planItems = data.implementationPlan ?? [];
  const inheritedDocumentLinks = useChangeDocumentLinks();
  const localDocumentLinks = useMemo(() => getChangeDocumentLinks(data), [data]);
  const documentLinks = inheritedDocumentLinks.length ? inheritedDocumentLinks : localDocumentLinks;
  const [expandedActivityId, setExpandedActivityId] = useState<string | undefined>();
  const [activityStatuses, setActivityStatuses] = useState<Record<string, string>>({});
  const [proposalStates, setProposalStates] = useState<Record<string, TImplementationProposalState>>(() =>
    getInitialImplementationProposalStates(planItems)
  );
  const [activeProposalActivityKey, setActiveProposalActivityKey] = useState<string | undefined>();
  const [viewedProposalDiffIds, setViewedProposalDiffIds] = useState<Record<string, string[]>>({});
  const [proposalConsent, setProposalConsent] = useState<Record<string, boolean>>({});
  const [selectedProposalDiff, setSelectedProposalDiff] = useState<{
    diff: TImplementationProposalDiff;
    diffKey: string;
  }>();
  const proposalLoadingTimeoutsRef = useRef<Record<string, number>>({});

  const clearProposalLoadingTimeout = (activityKey: string) => {
    const timeoutId = proposalLoadingTimeoutsRef.current[activityKey];
    if (timeoutId === undefined) return;

    window.clearTimeout(timeoutId);
    delete proposalLoadingTimeoutsRef.current[activityKey];
  };

  useEffect(
    () => () => {
      Object.values(proposalLoadingTimeoutsRef.current).forEach((timeoutId) => window.clearTimeout(timeoutId));
      proposalLoadingTimeoutsRef.current = {};
    },
    []
  );

  const activities: TImplementationActivityView[] = planItems.map((item, index) => {
    const key = getImplementationActivityKey(item, index);
    const displayState = getImplementationActivityDisplayState(item, planItems, activityStatuses, index);
    const dependencyIds = getImplementationActivityDependencyIds(item);
    const unresolvedDependencyIds = getUnresolvedImplementationDependencyIds(item, planItems, activityStatuses);

    return {
      item,
      key,
      displayState,
      dependencyIds,
      unresolvedDependencyIds,
      dependencyLabels: dependencyIds.map((id) => getDependencyLabel(id, planItems)),
      compactDependencyLabel: getCompactImplementationDependencyLabel({
        dependencyIds,
        unresolvedDependencyIds,
        displayState,
      }),
    };
  });
  const completeCount = activities.filter(
    (activity) => activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.COMPLETED
  ).length;
  const readyActivities = activities.filter(
    (activity) => activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY
  );
  const waitingCount = activities.filter(
    (activity) => activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.WAITING
  ).length;
  const blockedActivities = activities.filter(
    (activity) => activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.BLOCKED
  );
  const progressRatio = planItems.length ? completeCount / planItems.length : 0;
  const planStatus =
    completeCount === planItems.length && planItems.length
      ? ACTIVITY_STATUS.COMPLETE
      : blockedActivities.length
        ? ACTIVITY_STATUS.BLOCKED
        : CHANGE_STATUS.IN_EXECUTION;

  const handleToggleExpandedActivity = (activityKey: string) => {
    setExpandedActivityId((current) => (current === activityKey ? undefined : activityKey));
  };

  const handleActivityRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, activityKey: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleToggleExpandedActivity(activityKey);
  };

  const handleStartActivity = (activity: TImplementationActivityView) => {
    if (activity.displayState !== IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY) return;

    setExpandedActivityId(activity.key);
    setActivityStatuses((current) => ({ ...current, [activity.key]: ACTIVITY_STATUS.IN_PROGRESS }));
  };

  const handleRequestImplementationProposal = (activity: TImplementationActivityView) => {
    if (!canRequestImplementationProposal(activity.item, activity.displayState)) return;

    const currentProposalState =
      proposalStates[activity.key] ?? getImplementationProposalInitialState(activity.item.implementationProposal);

    if (currentProposalState === 'LOADING') return;

    if (currentProposalState === 'READY_FOR_REVIEW') {
      if (activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY) {
        setActivityStatuses((current) => ({ ...current, [activity.key]: ACTIVITY_STATUS.IN_PROGRESS }));
      }
      setExpandedActivityId(activity.key);
      setActiveProposalActivityKey(activity.key);
      return;
    }

    setViewedProposalDiffIds((current) => ({ ...current, [activity.key]: [] }));
    setProposalConsent((current) => ({
      ...current,
      [activity.key]: Boolean(activity.item.implementationProposal?.reviewGate?.consentInitiallyChecked),
    }));

    clearProposalLoadingTimeout(activity.key);
    setProposalStates((current) => ({ ...current, [activity.key]: 'LOADING' }));
    setExpandedActivityId(activity.key);
    setActiveProposalActivityKey(undefined);

    proposalLoadingTimeoutsRef.current[activity.key] = window.setTimeout(() => {
      delete proposalLoadingTimeoutsRef.current[activity.key];
      setActivityStatuses((current) => ({ ...current, [activity.key]: ACTIVITY_STATUS.IN_PROGRESS }));
      setProposalStates((current) =>
        current[activity.key] === 'LOADING' ? { ...current, [activity.key]: 'READY_FOR_REVIEW' } : current
      );
      setExpandedActivityId(activity.key);
      setActiveProposalActivityKey(activity.key);
    }, MOCK_IMPLEMENTATION_PROPOSAL_LOADING_MS);
  };

  const handleActivityPrimaryAction = (activity: TImplementationActivityView) => {
    if (canRequestImplementationProposal(activity.item, activity.displayState)) {
      handleRequestImplementationProposal(activity);
      return;
    }

    handleStartActivity(activity);
  };

  const handleOpenProposalDiff = (
    activity: TImplementationActivityView,
    diff: TImplementationProposalDiff,
    diffKey: string
  ) => {
    setViewedProposalDiffIds((current) => {
      const existing = current[activity.key] ?? [];
      return existing.includes(diffKey) ? current : { ...current, [activity.key]: [...existing, diffKey] };
    });
    setSelectedProposalDiff({ diff, diffKey });
  };

  const handleAcceptImplementationProposal = (activity: TImplementationActivityView) => {
    const proposal = activity.item.implementationProposal;
    const gateState = getImplementationProposalReviewGateState({
      proposal,
      viewedDiffIds: viewedProposalDiffIds[activity.key] ?? [],
      consentChecked: Boolean(proposalConsent[activity.key]),
    });

    if (!gateState.canAccept) return;

    clearProposalLoadingTimeout(activity.key);
    setActivityStatuses((current) => ({ ...current, [activity.key]: getAcceptedImplementationStatus(proposal) }));
    setProposalStates((current) => ({ ...current, [activity.key]: 'ACCEPTED' }));
    setActiveProposalActivityKey(undefined);
    setSelectedProposalDiff(undefined);
  };

  return (
    <ChangeDocumentLinksContext.Provider value={documentLinks}>
      <div className={classes.executionLayout}>
        <Card className={classes.surface} size="large">
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitleStack}>
              <Subtitle2>Plan summary</Subtitle2>
              <Caption1 className={classes.muted}>Implementation plan</Caption1>
            </div>
            <div className={classes.executionActions}>
              <Button appearance="secondary" icon={<DocumentTextRegular />} onClick={noop}>
                View assessment
              </Button>
              <Button appearance="secondary" icon={<CheckmarkCircleRegular />} onClick={noop}>
                View approval
              </Button>
              <Button appearance="secondary" icon={<EditRegular />} onClick={noop}>
                Request plan change
              </Button>
            </div>
          </div>

          <div className={classes.executionSummaryGrid}>
            <div className={classes.labelStack}>
              <Caption1 className={classes.muted}>Status</Caption1>
              <div>
                <ChangeStatusBadge status={planStatus} size="large" />
              </div>
            </div>
            <div className={classes.summaryProgress}>
              <Caption1 className={classes.muted}>Progress</Caption1>
              <Body1Strong>
                {completeCount} of {planItems.length} completed
              </Body1Strong>
              <ProgressBar value={progressRatio} thickness="medium" shape="rounded" />
            </div>
            <DetailValue label="Ready now">{`${readyActivities.length} ready now`}</DetailValue>
            <DetailValue label="Waiting">{`${waitingCount} waiting for dependencies`}</DetailValue>
            <DetailValue label="Plan approval">
              Approved {formatSignatureDate(signature.signedAt)} by {signature.signer}
            </DetailValue>
            {changeRequest.targetDate && (
              <DetailValue label="Target date">{formatOptionalDate(changeRequest.targetDate)}</DetailValue>
            )}
          </div>

          <div className={classes.summarySecondaryMeta}>
            <Caption1>Plan version {MOCK_IMPLEMENTATION_PLAN_APPROVED_VERSION}</Caption1>
          </div>

          {blockedActivities.length ? (
            <div className={classes.labelStack}>
              <Caption1 className={classes.muted}>Exceptional blockers</Caption1>
              <SimpleList
                items={blockedActivities.map((activity) =>
                  activity.item.blockerReason
                    ? `${getActivityTitle(activity.item)}: ${activity.item.blockerReason}`
                    : getActivityTitle(activity.item)
                )}
                limit={5}
              />
            </div>
          ) : null}
        </Card>

        <Card className={classes.surface} size="large">
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitleStack}>
              <Subtitle2>Ready now</Subtitle2>
              <Caption1 className={classes.muted}>
                Activities that can be started without completing another plan step.
              </Caption1>
            </div>
          </div>

          {readyActivities.length ? (
            <div className={classes.readyCardsGrid}>
              {readyActivities.map((activity) => {
                const isPrimaryDemoAction = Boolean(activity.item.primaryDemoAction);
                const proposalState =
                  proposalStates[activity.key] ??
                  getImplementationProposalInitialState(activity.item.implementationProposal);
                const isProposalLoading = proposalState === 'LOADING';

                return (
                  <div
                    key={activity.key}
                    data-activity-key={activity.key}
                    className={mergeClasses(
                      classes.readyActivityCard,
                      isPrimaryDemoAction && classes.primaryReadyActivityCard
                    )}
                  >
                    <div className={classes.readyCardBody}>
                      <div className={classes.readyCardHeader}>
                        <Caption1 className={classes.activityId}>{activity.item.id ?? activity.key}</Caption1>
                        <div className={classes.readyCardBadges}>
                          <ActivityStateBadge state={activity.displayState} />
                          <ActionTypeBadge item={activity.item} />
                        </div>
                      </div>
                      <Subtitle2 className={classes.valueText}>{activity.item.activity}</Subtitle2>
                      <div className={classes.detailGrid}>
                        <TextDetailValue label="Responsible function" value={activity.item.responsibleFunction} />
                        <div className={classes.labelStack}>
                          <Caption1 className={classes.muted}>Assigned user</Caption1>
                          <AssigneeDisplay item={activity.item} users={users} />
                        </div>
                        <TextDetailValue
                          label="Deliverable"
                          value={getImplementationShortDeliverable(activity.item)}
                          className={classes.fullWidth}
                        />
                      </div>
                      {isPrimaryDemoAction && (
                        <Caption1 className={classes.controlNote}>{getHumanControlNote(activity.item)}</Caption1>
                      )}
                    </div>
                    <div className={classes.actionRow}>
                      <ButtonLoading
                        appearance={isPrimaryDemoAction ? 'primary' : 'secondary'}
                        icon={getActivityActionIcon(activity)}
                        loading={isProposalLoading}
                        onClick={() => handleActivityPrimaryAction(activity)}
                      >
                        {getReadyActivityActionLabel(activity)}
                      </ButtonLoading>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Body1>No activities are ready right now.</Body1>
          )}
        </Card>

        <Card className={classes.surface} size="large">
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitleStack}>
              <Subtitle2>Complete implementation plan</Subtitle2>
              <Caption1 className={classes.muted}>{getCountLabel(planItems.length, 'activity', 'activities')}</Caption1>
            </div>
          </div>

          <div className={classes.activityList}>
            {activities.map((activity, index) => {
              const isExpanded = expandedActivityId === activity.key;
              const canUseActivityAction = getActivityActionEnabled(activity);
              const isPrimaryDemoAction = Boolean(activity.item.primaryDemoAction);
              const proposal = activity.item.implementationProposal;
              const proposalState = proposalStates[activity.key] ?? getImplementationProposalInitialState(proposal);
              const isProposalLoading = proposalState === 'LOADING';
              const isCompleted = activity.displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.COMPLETED;
              const isProposalVisible = Boolean(
                proposal && isProposalReviewVisible(proposalState, activeProposalActivityKey, activity.key)
              );
              const isProposalCompletionVisible = Boolean(
                isCompleted && proposalState === 'ACCEPTED' && proposal?.completionMessage
              );
              const shouldShowActivityDetails =
                isExpanded && (isProposalVisible || isProposalCompletionVisible || canUseActivityAction);

              return (
                <div
                  key={activity.key}
                  data-activity-key={activity.key}
                  className={mergeClasses(
                    classes.activityRow,
                    isExpanded && classes.expandedActivityRow,
                    isPrimaryDemoAction && classes.primaryActivityRow
                  )}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    className={classes.activityRowButton}
                    onClick={() => handleToggleExpandedActivity(activity.key)}
                    onKeyDown={(event) => handleActivityRowKeyDown(event, activity.key)}
                  >
                    <div className={classes.stepIndicator}>{activity.item.id ?? index + 1}</div>
                    <div className={classes.activityMain}>
                      <div className={classes.activityTitleLine}>
                        <Body1Strong className={classes.valueText}>{activity.item.activity}</Body1Strong>
                        <ActionTypeBadge item={activity.item} />
                      </div>
                      <Caption1 className={classes.documentsText}>
                        <DocumentReferenceText text={getImplementationShortDeliverable(activity.item)} />
                      </Caption1>
                      <Caption1 className={classes.documentsText}>{activity.compactDependencyLabel}</Caption1>
                    </div>
                    <div className={classes.activityAssignment}>
                      <Caption1 className={classes.muted}>Responsible function</Caption1>
                      <Body1Strong className={classes.valueText}>
                        {activity.item.responsibleFunction || '-'}
                      </Body1Strong>
                      <AssigneeDisplay item={activity.item} users={users} />
                    </div>
                    <div className={classes.activityStatusColumn}>
                      <ActivityStateBadge state={activity.displayState} />
                      <ButtonLoading
                        appearance={canUseActivityAction && isPrimaryDemoAction ? 'primary' : 'secondary'}
                        disabled={!canUseActivityAction}
                        icon={getActivityActionIcon(activity)}
                        loading={isProposalLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleActivityPrimaryAction(activity);
                        }}
                      >
                        {getActivityActionLabel(activity)}
                      </ButtonLoading>
                      {isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                    </div>
                  </div>

                  {shouldShowActivityDetails && (
                    <div className={classes.activityDetails}>
                      {isProposalCompletionVisible && proposal?.completionMessage ? (
                        <div className={classes.proposalCompletion}>
                          <Body1Strong>Implementation proposal accepted</Body1Strong>
                          <Body1 className={classes.valueText}>
                            <DocumentReferenceText text={proposal.completionMessage} />
                          </Body1>
                        </div>
                      ) : null}

                      {proposal && isProposalVisible && (
                        <ImplementationProposalPanel
                          proposal={proposal}
                          viewedDiffIds={viewedProposalDiffIds[activity.key] ?? []}
                          consentChecked={Boolean(proposalConsent[activity.key])}
                          onOpenDiff={(diff, diffKey) => handleOpenProposalDiff(activity, diff, diffKey)}
                          onConsentChange={(checked) =>
                            setProposalConsent((current) => ({ ...current, [activity.key]: checked }))
                          }
                          onAccept={() => handleAcceptImplementationProposal(activity)}
                          onClose={() => setActiveProposalActivityKey(undefined)}
                        />
                      )}

                      {!isProposalVisible && !isProposalCompletionVisible && canUseActivityAction ? (
                        <ButtonLoading
                          className={classes.showMoreButton}
                          appearance={canUseActivityAction && isPrimaryDemoAction ? 'primary' : 'secondary'}
                          icon={getActivityActionIcon(activity)}
                          loading={isProposalLoading}
                          onClick={() => handleActivityPrimaryAction(activity)}
                        >
                          {getActivityActionLabel(activity)}
                        </ButtonLoading>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <ImplementationProposalDiffDialog
          selectedDiff={selectedProposalDiff}
          onClose={() => setSelectedProposalDiff(undefined)}
        />
      </div>
    </ChangeDocumentLinksContext.Provider>
  );
};

const ChangeAssessmentScreen = ({
  folder,
  fullPath,
  config,
  changeRequest,
  existingChange,
  startAiAssessment,
}: TProps) => {
  const classes = useStyles();
  const { user } = useContext(SignInContext);
  const request = changeRequest ?? getFallbackChangeRequest(folder, existingChange);
  const assessmentConfig = getAssessmentConfig(config.assessment);
  const shouldStartAiAssessment = Boolean(changeRequest) && (startAiAssessment ?? config.startAiAssessment ?? true);
  const [phase, setPhase] = useState<TAssessmentPhase>(shouldStartAiAssessment ? 'initial-assessing' : 'idle');
  const [progress, setProgress] = useState(shouldStartAiAssessment ? 0 : 1);
  const [messageProgressRatio, setMessageProgressRatio] = useState(0);
  const [informationAnswers, setInformationAnswers] = useState<Record<string, string>>({});
  const [approvalSignature, setApprovalSignature] = useState<TMockApprovalSignature>();
  const [isSigningApproval, setIsSigningApproval] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const informationRequests = assessmentConfig.informationRequests;
  const hasInformationRequests = informationRequests.length > 0;
  const hasAnsweredRequiredInformationRequests = informationRequests.every(
    (request) => !request.required || Boolean(informationAnswers[request.id])
  );
  const isAssessmentRunning = phase === 'initial-assessing' || phase === 'continuation-assessing';

  useEffect(() => {
    if (!isAssessmentRunning) return undefined;

    const startedAt = Date.now();
    const duration = phase === 'initial-assessing' ? assessmentConfig.questionDelay : assessmentConfig.completionDelay;

    const intervalId = window.setInterval(() => {
      const elapsedRatio = Math.min((Date.now() - startedAt) / Math.max(duration, 1), 1);
      setMessageProgressRatio(elapsedRatio);
      setProgress(elapsedRatio);

      if (elapsedRatio < 1) return;

      window.clearInterval(intervalId);
      setPhase(phase === 'initial-assessing' && hasInformationRequests ? 'questions' : 'complete');
      setProgress(1);
    }, 120);

    return () => window.clearInterval(intervalId);
  }, [
    assessmentConfig.completionDelay,
    assessmentConfig.questionDelay,
    hasInformationRequests,
    isAssessmentRunning,
    phase,
  ]);

  useEffect(() => {
    if (phase !== 'complete' || !hasInformationRequests || hasAnsweredRequiredInformationRequests) return;

    setPhase('questions');
    setProgress(1);
    setMessageProgressRatio(1);
  }, [hasAnsweredRequiredInformationRequests, hasInformationRequests, phase]);

  useEffect(() => {
    if (!approvalSignature) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [approvalSignature]);

  const assessmentData = config.newData ?? {};
  const documentLinks = useMemo(() => getChangeDocumentLinks(assessmentData), [assessmentData]);
  const status = approvalSignature
    ? CHANGE_STATUS.IN_EXECUTION
    : phase === 'complete'
      ? assessmentData.status || CHANGE_STATUS.ASSESSMENT_COMPLETE_PENDING_APPROVAL
      : phase === 'questions'
        ? CHANGE_STATUS.ADDITIONAL_INFORMATION_REQUIRED
        : isAssessmentRunning
          ? CHANGE_STATUS.AI_ASSESSMENT_IN_PROGRESS
          : existingChange?.status || CHANGE_STATUS.DRAFT;
  const isComplete = phase === 'complete';
  const isAdditionalInformationRequired = phase === 'questions';
  const currentMessages =
    phase === 'continuation-assessing' ? assessmentConfig.continuationMessages : assessmentConfig.messages;
  const currentMessage =
    phase === 'idle' ? 'Assessment has not been started' : getCurrentMessage(currentMessages, messageProgressRatio);
  const productLabel = request.product || folder.name;
  const requiredSigner = getSignerLabel(user);

  const handleInformationAnswerChange = (id: string, value: string) => {
    setInformationAnswers((current) => ({
      ...current,
      [id]: value,
    }));
  };

  const handleContinueAssessment = () => {
    setProgress(0);
    setMessageProgressRatio(0);
    setPhase('continuation-assessing');
  };

  const handleApproveForExecution = async () => {
    if (isSigningApproval) return;

    setIsSigningApproval(true);
    try {
      await wait(MOCK_APPROVAL_LOADING_MS);
      setApprovalSignature({
        signer: requiredSigner,
        signedAt: new Date().toISOString(),
        meaning: CHANGE_ASSESSMENT_SIGNATURE_MEANING,
      });
    } finally {
      setIsSigningApproval(false);
    }
  };

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={folder} fullPath={fullPath} selectedValue="changes" />

      <div className="grow h-full w-full relative">
        <div ref={scrollContainerRef} className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className={mergeClasses('flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8', classes.content)}>
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />

            <div className={classes.pageStack}>
              <div className={classes.headerRow}>
                <div className={classes.headerTitle}>
                  <Caption1 className={classes.muted}>{request.changeNumber}</Caption1>
                  <Subtitle1>{request.changeTitle}</Subtitle1>
                </div>
              </div>

              <ChangeDocumentLinksContext.Provider value={documentLinks}>
                <div className={classes.mainStack}>
                  <Card className={classes.surface} size="large">
                    <div className={classes.sectionHeader}>
                      <Subtitle2>Change details</Subtitle2>
                      <SectionEditButton label="Change details" />
                    </div>

                    <div className={classes.metaGrid}>
                      <DetailValue label="Product">{productLabel}</DetailValue>
                      <div className={classes.labelStack}>
                        <Caption1 className={classes.muted}>Status</Caption1>
                        <div>
                          <ChangeStatusBadge status={status} size="large" />
                        </div>
                      </div>
                      <div className={classes.labelStack}>
                        <Caption1 className={classes.muted}>Owner</Caption1>
                        <div className={classes.ownerRow}>
                          <Avatar name={request.owner} color="colorful" size={28} />
                          <Body1Strong className={classes.valueText}>{request.owner}</Body1Strong>
                        </div>
                      </div>
                      <DetailValue label="Target date">{formatOptionalDate(request.targetDate)}</DetailValue>
                    </div>

                    <div className={classes.cardDivider} />

                    <div className={classes.detailGrid}>
                      <DetailValue label="Change title">{request.changeTitle}</DetailValue>
                      <DetailValue label="Trigger">{request.trigger}</DetailValue>
                      <TextDetailValue
                        label="Change description"
                        value={request.description}
                        className={classes.fullWidth}
                      />

                      {shouldStartAiAssessment && !isComplete && (
                        <>
                          <DetailValue label="Change type">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                          <DetailValue label="Change subtype">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                          <DetailValue label="Impact level">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                          <DetailValue label="Affected areas">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                          <DetailValue label="Regulatory impact">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                          <DetailValue label="Safety and performance impact">{CHANGE_BEING_ASSESSED_LABEL}</DetailValue>
                        </>
                      )}
                    </div>
                  </Card>

                  {shouldStartAiAssessment && isAssessmentRunning && (
                    <Card className={classes.surface} size="large">
                      <div className={classes.sectionHeader}>
                        <Subtitle2>Assessment</Subtitle2>
                        <BrainSparkleRegular className={classes.assessmentHeaderIcon} />
                      </div>

                      <Body1>Safety, performance, regulatory and documentation impacts are being assessed.</Body1>
                      <div className={classes.progressMeta}>
                        <Caption1>In progress</Caption1>
                        <Caption1>{Math.round(progress * 100)}%</Caption1>
                      </div>
                      <ProgressBar value={progress} thickness="large" shape="rounded" />
                      <Body1 className={classes.progressMessage}>{currentMessage}</Body1>
                    </Card>
                  )}

                  {isAdditionalInformationRequired && (
                    <InformationRequestsCard
                      requests={informationRequests}
                      answers={informationAnswers}
                      onAnswerChange={handleInformationAnswerChange}
                      onContinue={handleContinueAssessment}
                    />
                  )}

                  {isComplete && approvalSignature ? (
                    <ImplementationPlanExecutionView
                      data={assessmentData}
                      changeRequest={request}
                      signature={approvalSignature}
                    />
                  ) : isComplete ? (
                    <>
                      <AssessmentResultContent data={assessmentData} />
                      <ImplementationPlanContent data={assessmentData} />
                      <AssessmentApprovalCard
                        signature={approvalSignature}
                        onApproveForExecution={handleApproveForExecution}
                        changeRequest={request}
                        requiredSigner={requiredSigner}
                        isSigning={isSigningApproval}
                      />
                    </>
                  ) : null}
                </div>
              </ChangeDocumentLinksContext.Provider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeAssessmentScreen;
